/**
 * src/domain/value-objects/locale.ts
 *
 * Locale — SecureSys'in desteklediği dillerin Domain tipi.
 *
 * β.2.5 ile **dinamik registry'ye hizalandı**: önceki hardcoded `'tr' | 'en'`
 * union literal yerine `string`. Geçerli code'lar runtime'da
 * `siteSettings.locales[].code` listesinden gelir; admin Site Settings UI'da
 * (β.4) yeni dil eklendiğinde Domain tip katmanı dokunulmaz.
 *
 * Pure TS (Domain layer) — framework import YOK. Domain registry'nin nereden
 * okunduğunu bilmez; geçerlilik kontrolü için caller registry'yi parametre
 * olarak geçirir.
 *
 * Referans: ADR-0002, spec `2026-05-26-phase-beta-2-5-schema-rework-design.md` §3.
 */

/**
 * Locale code — runtime string. Tipin kendisi sadece "locale code anlam taşıyan
 * string" sembolüdür; tip seviyesinde kısıt yok. Runtime disiplin uygulama
 * boundary'lerinde Zod schema ile registry'ye karşı doğrulanır.
 */
export type Locale = string

/**
 * Domain'in son-çare fallback locale'i.
 *
 * Bu **bir varsayılan değer değil**, defensive programming sigortasıdır:
 * - `pickLocalized` çağrılırken caller fallback geçirmezse bu kullanılır
 * - siteSettings okunamadığında (DB outage, cache miss, boot-time before-init)
 *   sistem hâlâ render edebilsin diye sabit bir referans noktası
 *
 * Adlandırma kasıtlı: `DEFAULT_LOCALE` değil `DOMAIN_FALLBACK_LOCALE` — bu,
 * application katmanının `siteSettings.defaultLocale`'inden farklıdır. Caller
 * authoritative default'u biliyorsa (örn. async `getDefaultLocale()` ile
 * siteSettings'ten okuyorsa) explicit geçirerek bunu override etmelidir.
 *
 * Değişme nedeni: SecureSys TR-merkezli kurumsal site; tüm legacy içerik TR
 * öncelikli. Bu sabit, "data layer her şeyi kaybetse bile render olabilen
 * minimum yapı" garantisidir.
 */
export const DOMAIN_FALLBACK_LOCALE: Locale = 'tr'

/**
 * Verilen değerin geçerli bir locale code olup olmadığını runtime'da kontrol eder.
 *
 * Caller geçerli code listesini ikinci argüman olarak geçirir (örn. Server
 * Component'te `const locales = await getValidLocales()`). Domain registry'nin
 * kaynağını bilmez — siteSettings'e bağımsız kalır.
 *
 * @param value Herhangi bir input — URL segment, form input, vb.
 * @param registry Caller'ın sağladığı geçerli locale code listesi
 * @returns `value` bir string ve `registry` içinde varsa `true`; aksi halde `false`.
 *          Case-sensitive (`'TR'` ile `'tr'` farklı sayılır).
 *
 * @example
 * ```ts
 * const locales = await getValidLocales()    // ['tr', 'en']
 * if (!isValidLocale(urlSegment, locales)) notFound()
 * ```
 */
export function isValidLocale(
  value: unknown,
  registry: readonly string[],
): boolean {
  return typeof value === 'string' && registry.includes(value)
}
